import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, AlertTriangle, ArrowLeftRight, Inbox, FilePlus, DollarSign, Boxes, Building2, Truck } from 'lucide-react';
import { inv } from '../../lib/invApi';
import ScanButton from '../../components/inventory/ScanButton';

const Tile = ({ label, value, accent, icon: Icon, testId, sub }) => (
  <div data-testid={testId} className="bg-[#141414] border border-[#262626] rounded-md p-4 flex items-start gap-3">
    {Icon && <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-md flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#D4AF37]" /></div>}
    <div className="min-w-0">
      <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-white'}`}>{value}</p>
      {sub && <p className="text-[#525252] text-xs mt-0.5">{sub}</p>}
    </div>
  </div>
);

const InventoryDashboard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    inv.get('/dashboard').then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || 'Failed to load'));
  }, []);

  if (err) return <div className="text-red-400">{String(err)}</div>;
  if (!data) return <div className="text-[#A3A3A3]">Loading…</div>;

  return (
    <div data-testid="inv-dashboard-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Boxes className="w-7 h-7 text-[#D4AF37]" /> Inventory Overview
          </h2>
          <p className="text-[#A3A3A3]">Shop/Warehouse = exact · Van Transfer History = factual · Estimated On Van = rough reference only.</p>
        </div>
        <ScanButton mode="scan" testId="dashboard-scan-btn" />
      </div>

      {/* Value */}
      <section>
        <h3 className="text-white text-sm uppercase tracking-wider mb-2">Exact Inventory Value (Shop + Warehouse)</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile testId="inv-value-shop" label="Shop" value={`$${data.value.shop.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={Building2} accent="text-[#D4AF37]" />
          <Tile testId="inv-value-warehouse" label="Warehouse" value={`$${data.value.warehouse.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={Building2} />
          <Tile testId="inv-value-total" label="Exact Company Total" value={`$${data.value.exact_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={DollarSign} accent="text-green-400" sub="Shop + Warehouse only" />
          <Link to="/admin/inventory-hub/van-transfers" className="block">
            <Tile testId="inv-van-transfers-30d" label="Transferred to Vans · 30 days" value={`${data.van_transfers_30d.quantity.toLocaleString()} pcs`} icon={Truck} sub={`${data.van_transfers_30d.transfers} transfers · $${data.van_transfers_30d.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
          </Link>
          <Tile testId="inv-value-van-estimated" label="Estimated On Van (rough)" value={`~$${data.value.van_estimated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={Package} accent="text-[#737373]" sub="Not counted in company inventory" />
        </div>
      </section>

      {/* Counts */}
      <section>
        <h3 className="text-white text-sm uppercase tracking-wider mb-2">Workflow</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/admin/inventory-hub/reorder" className="block">
            <Tile testId="inv-low-stock-count" label="Company Low Stock" value={data.low_stock_count} icon={AlertTriangle} accent={data.low_stock_count > 0 ? 'text-amber-400' : 'text-[#525252]'} />
          </Link>
          <Link to="/admin/inv/material-requests" className="block">
            <Tile testId="inv-pending-material" label="Material Requests" value={data.pending_material_requests} icon={Inbox} accent={data.pending_material_requests > 0 ? 'text-[#D4AF37]' : 'text-[#525252]'} />
          </Link>
          <Link to="/admin/inv/new-item-requests" className="block">
            <Tile testId="inv-pending-new-item" label="New Item Requests" value={data.pending_new_item_requests} icon={FilePlus} accent={data.pending_new_item_requests > 0 ? 'text-[#D4AF37]' : 'text-[#525252]'} />
          </Link>
          <Link to="/admin/inv/transfers" className="block">
            <Tile testId="inv-pending-transfers" label="Transfers Pending" value={data.pending_transfers} icon={ArrowLeftRight} accent={data.pending_transfers > 0 ? 'text-[#D4AF37]' : 'text-[#525252]'} />
          </Link>
        </div>
      </section>

      {/* Low stock preview */}
      <section>
        <h3 className="text-white text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Company Low Stock (first 20)
          <span className="normal-case tracking-normal text-[#525252] text-xs">Company Minimum vs Shop + Warehouse On Hand — vans never count</span>
          <Link to="/admin/inventory-hub/reorder" data-testid="inv-open-reorder" className="ml-auto normal-case tracking-normal text-xs text-[#D4AF37] hover:underline">Open Reorder List →</Link>
        </h3>
        {data.low_stock_preview.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center text-[#525252] text-sm">Everything is at or above its Company Minimum.</div>
        ) : (
          <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                <tr>
                  <th className="text-left px-3 py-2 text-[#A3A3A3]">Item</th>
                  <th className="text-left px-3 py-2 text-[#A3A3A3]">Supplier</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Shop + WH On Hand</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Company Min</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Suggested Order</th>
                  <th className="text-right px-3 py-2 text-[#A3A3A3]">Pending</th>
                </tr>
              </thead>
              <tbody>
                {data.low_stock_preview.map((r, i) => (
                  <tr key={`${r.item_id}-${i}`} data-testid={`inv-low-row-${r.item_id}`} className="border-b border-[#262626] last:border-0">
                    <td className="px-3 py-2 text-white">{r.item_name}</td>
                    <td className="px-3 py-2 text-[#A3A3A3]">{r.supplier || 'Supplier Not Assigned'}</td>
                    <td className="px-3 py-2 text-right text-amber-400 font-bold">{r.controlled_on_hand}</td>
                    <td className="px-3 py-2 text-right text-[#A3A3A3]">{r.company_min}</td>
                    <td className="px-3 py-2 text-right text-[#D4AF37] font-semibold">{r.suggested_order}</td>
                    <td className="px-3 py-2 text-right text-[#D4AF37]">{r.pending_requests > 0 ? `${r.pending_requests} pending` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-[#525252] text-xs">
        Inventory architecture: <span className="text-[#A3A3A3]">Item → Location → Quantity</span>. Every change is reason-coded and audit-logged.
      </p>
    </div>
  );
};

export default InventoryDashboard;
